import { createStore } from "zustand/vanilla";
import { compareByOrder } from "@pack-it-up/shared";
import { isCategoryCollapsed } from "@pack-it-up/core";
import { emptyReconcile, onSnapshot, list, get as rget, } from "./reconcile.js";
import { memoizedOverSlice } from "./memo.js";
import { createOptimisticRunner } from "./optimistic.js";
import { errorToMessage } from "../errorToMessage.js";
export const createCategoriesStore = (deps) => {
    const sorted = memoizedOverSlice((s) => list(s).sort(compareByOrder));
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set, getState) => {
        const runner = createOptimisticRunner({
            entity: "category",
            deltaPrefix: "cat-delta",
            getSlice: () => getState().slice,
            set,
            ...(deps.writeTimeoutMs !== undefined ? { timeoutMs: deps.writeTimeoutMs } : {}),
        });
        const optimistic = runner.mutate;
        return {
            slice: emptyReconcile(),
            error: null,
            categories: () => sorted(getState().slice),
            async create(input) {
                const r = await deps.categories.createCategory(input);
                if (!r.ok)
                    set({ error: errorToMessage(r.error) });
                return r;
            },
            update: (patch) => optimistic(patch.categoryId, (cur) => {
                const next = {
                    ...cur,
                    ...(patch.name !== undefined ? { name: patch.name } : {}),
                    ...(patch.order !== undefined ? { order: patch.order } : {}),
                };
                // emoji is a three-state patch: undefined leaves it, null clears it, a
                // value sets it (same LWW convention as other optional scalars).
                if (patch.emoji === null)
                    delete next.emoji;
                else if (patch.emoji !== undefined)
                    next.emoji = patch.emoji;
                return next;
            }, () => deps.categories.updateCategory(patch)),
            isCollapsed: (category) => isCategoryCollapsed(category, deps.actor),
            toggleCollapsed: (categoryId) => {
                const cur = rget(getState().slice, categoryId);
                const collapsed = !(cur !== undefined && isCategoryCollapsed(cur, deps.actor));
                return optimistic(categoryId, (c) => {
                    const collapsedBy = { ...c.collapsedBy };
                    if (collapsed)
                        collapsedBy[deps.actor] = true;
                    else
                        delete collapsedBy[deps.actor];
                    return { ...c, collapsedBy };
                }, () => deps.categories.setCollapsed(categoryId, deps.actor, collapsed));
            },
            reorder: (categoryId, order) => optimistic(categoryId, (c) => ({ ...c, order }), () => deps.categories.reorderCategory(categoryId, order)),
            remove: (categoryId) => runner.remove(categoryId, () => deps.categories.deleteCategory(categoryId)),
            dispose() {
                for (const unsubscribe of subs.splice(0))
                    unsubscribe();
            },
        };
    });
    subs.push(deps.categories.observeCategories(deps.parentId).subscribe((cats) => {
        store.setState({ slice: onSnapshot(store.getState().slice, cats, (c) => c.id) });
    }));
    return store;
};
//# sourceMappingURL=categoriesStore.js.map